import {useCallback, useMemo} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {v4} from 'uuid';
import {
  TOGGLE_SHOW_VALIDATION_MODAL,
  UPDATE_SHEET_DETAILS,
} from '~/SignUpSheets/reducers/sheet/constants';
import {selectErrors, selectShowValidationModal} from '~/SignUpSheets/reducers/sheet/selectors';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';

export const useValidation = () => {
  const dispatch = useDispatch();
  const errorMessages = useSelector(selectErrors, shallowEqual);
  const showValidationModal = useSelector(selectShowValidationModal);

  const hideValidationModal = useCallback(() => {
    dispatch({
      type: TOGGLE_SHOW_VALIDATION_MODAL,
      payload: false,
    });
  }, [dispatch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const goToErrors = useCallback(() => {
    dispatch({
      type: UPDATE_SHEET_DETAILS,
      payload: {previewMode: false},
    });

    const errorElements = document.querySelectorAll('.input-error');
    if (errorElements.length > 0) {
      errorElements[0].scrollIntoView({
        block: 'center',
        behavior: 'smooth',
      });
    }
    hideValidationModal();
  }, [hideValidationModal]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const error = useMemo(
    () => ({
      errorMessages,
      error: true,
      title: 'Missing details',
      message: 'Your SignUp Sheet is missing some information.',
    }),
    [errorMessages, hideValidationModal]
  );

  const ValidationModal = useMemo(() => {
    const errorList =
      errorMessages?.length > 0 ? errorMessages.map((err) => <li key={v4()}>{err.msg}</li>) : null;

    const body = (
      <div>{errorList ? <ul>{errorList}</ul> : <p>No specific errors provided.</p>}</div>
    );

    return showValidationModal && errorMessages?.length > 0
      ? <ResponsiveModal
          isOpen={showValidationModal}
          title="Your SignUp Sheet is missing some information"
          primaryText="Fix my errors"
          primaryAction={goToErrors}
          secondaryText="Close"
          onClose={hideValidationModal}
          body={body}
        />
      : null;
  }, [errorMessages, hideValidationModal, showValidationModal, goToErrors]);

  return {error, ValidationModal};
};
